import React, { useState, useEffect } from 'react';
import { useCacheManager } from '../hooks/useCachedAPI';
import SecuredImage from './SecuredImage';

/**
 * Debug Panel component - only shows when DEBUG_MODE is enabled
 * Provides debugging tools and information
 */
export default function DebugPanel({ config, bearerToken, user, gitInfo }) {
    const [isVisible, setIsVisible] = useState(false);
    const [performance, setPerformance] = useState(null);
    const { stats, clearAll, cleanup } = useCacheManager();

    const isDebug = (typeof window !== 'undefined' && window.DEBUG_MODE) ||
        (import.meta.env && import.meta.env.VITE_DEBUG_MODE === 'true');

    // Only render in debug mode
    if (!isDebug) return null;

    // Performance monitoring
    useEffect(() => {
        if (window.performance) {
            const perfData = {
                navigation: window.performance.navigation,
                timing: window.performance.timing,
                memory: window.performance.memory || null
            };
            setPerformance(perfData);
        }
    }, []);

    // ESC key listener to dismiss panel
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isVisible) {
                setIsVisible(false);
            }
        };

        if (isVisible) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isVisible]);

    const toggleVisibility = () => setIsVisible(!isVisible);

    const logInfo = (message, data = null) => {
        console.log(`[DEBUG] ${message}`, data || '');
    };

    const clearConsole = () => {
        console.clear();
        logInfo('Console cleared');
    };

    const reloadPage = () => {
        window.location.reload();
    };

    return (
        <>
            {/* Debug Toggle Button */}
            <button
                onClick={toggleVisibility}
                className="fixed bottom-4 right-4 z-50 bg-yellow-500 hover:bg-yellow-600 text-black px-3 py-2 rounded-full shadow-lg text-sm font-mono"
                title="Toggle Debug Panel"
            >
                🐛 DEBUG
            </button>

            {/* Debug Panel */}
            {isVisible && (
                <div className="fixed bottom-16 right-4 z-50 bg-black bg-opacity-90 text-green-400 p-4 rounded-lg shadow-xl max-w-md max-h-96 overflow-y-auto font-mono text-xs">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-yellow-400 font-bold">🐛 Debug Panel</h3>
                        <button
                            onClick={toggleVisibility}
                            className="text-red-400 hover:text-red-300"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Debug Info Section */}
                    <div className="mb-4 rounded-lg bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100 border border-yellow-300 dark:border-yellow-700 px-4 py-2 text-xs font-mono opacity-90">
                        <div className="flex flex-col gap-1">
                            <div><strong>Debug Mode:</strong> ON</div>
                            <div><strong>API URL:</strong> <span className="break-all">{config?.apiBaseUrl || config?.baseUrl || config?.url || ''}</span></div>
                            <div><strong>Bearer Token:</strong> <span className="break-all">{bearerToken ? bearerToken : <em>None</em>}</span></div>
                            <div><strong>User Roles:</strong> <span>{user && Array.isArray(user.roles) ? (user.roles.length > 0 ? user.roles.join(', ') : <em>None</em>) : <em>None</em>}</span></div>
                            <div className="flex items-center gap-2 mt-1">
                                <strong>Avatar:</strong>
                                {user && user.avatar ? (
                                    <SecuredImage
                                        src={user.avatar}
                                        alt="User Avatar"
                                        className="inline-block size-6 rounded-full border border-gray-400"
                                        style={{ background: '#fff' }}
                                        fallback={
                                            <span className="inline-flex size-6 items-center justify-center rounded-full bg-emerald-700 text-xs font-bold text-white">
                                                {user?.initials || '?'}
                                            </span>
                                        }
                                    />
                                ) : (
                                    <span className="inline-flex size-6 items-center justify-center rounded-full bg-emerald-700 text-xs font-bold text-white">
                                        {user?.initials || '?'}
                                    </span>
                                )}
                                <strong className="ml-3">Initials:</strong> <span>{user?.initials || <em>None</em>}</span>
                            </div>
                            <div>
                                <strong>Avatar URL (raw):</strong> <span className="break-all">{user?.avatar || <em>None</em>}</span>
                            </div>

                            {/* Git information section */}
                            <div className="mt-2 pt-2 border-t border-yellow-300 dark:border-yellow-700">
                                <div><strong>Git Commit:</strong> <span className="break-all">{gitInfo?.commit}</span></div>
                                <div><strong>Git Branch:</strong> <span>{gitInfo?.branch}</span></div>
                                <div><strong>Git Tag:</strong> <span>{gitInfo?.tag || <em>None</em>}</span></div>
                                <div><strong>Commit Date:</strong> <span>{gitInfo?.date}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="mb-3">
                        <h4 className="text-blue-400 mb-1">Quick Actions:</h4>
                        <div className="flex gap-1 flex-wrap">
                            <button
                                onClick={clearConsole}
                                className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
                            >
                                Clear Console
                            </button>
                            <button
                                onClick={reloadPage}
                                className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
                            >
                                Reload
                            </button>
                            <button
                                onClick={() => logInfo('Current URL', window.location.href)}
                                className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
                            >
                                Log URL
                            </button>
                        </div>
                    </div>

                    {/* Cache Stats */}
                    {stats && (
                        <div className="mb-3">
                            <h4 className="text-blue-400 mb-1">Cache Stats:</h4>
                            <div className="bg-gray-800 p-2 rounded">
                                <div>Total: {stats.totalItems}</div>
                                <div>Valid: {stats.validItems}</div>
                                <div>Expired: {stats.expiredItems}</div>
                                <div className="flex gap-1 mt-1">
                                    <button
                                        onClick={clearAll}
                                        className="bg-red-600 hover:bg-red-500 px-1 py-0.5 rounded text-xs"
                                    >
                                        Clear Cache
                                    </button>
                                    <button
                                        onClick={cleanup}
                                        className="bg-yellow-600 hover:bg-yellow-500 px-1 py-0.5 rounded text-xs"
                                    >
                                        Cleanup
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Performance Info */}
                    {performance && (
                        <div className="mb-3">
                            <h4 className="text-blue-400 mb-1">Performance:</h4>
                            <div className="bg-gray-800 p-2 rounded">
                                <div>Load: {performance.timing.loadEventEnd - performance.timing.navigationStart}ms</div>
                                <div>DOM: {performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart}ms</div>
                                {performance.memory && (
                                    <div>Memory: {Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)}MB</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Environment Info */}
                    <div className="mb-3">
                        <h4 className="text-blue-400 mb-1">Environment:</h4>
                        <div className="bg-gray-800 p-2 rounded">
                            <div>Mode: {import.meta.env.MODE}</div>
                            <div>Debug: {isDebug ? 'ON' : 'OFF'}</div>
                            <div>Token: {localStorage.getItem('token') ? 'Present' : 'None'}</div>
                        </div>
                    </div>

                    {/* Quick Breakpoint Trigger */}
                    <div>
                        <button
                            onClick={() => {
                                // eslint-disable-next-line no-debugger
                                debugger; // This will trigger a breakpoint if DevTools is open
                            }}
                            className="bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-xs w-full"
                        >
                            🛑 Trigger Breakpoint
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
